import { useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/lib/auth-context";
import { moduloExigidoPelaRota, rotaInicialDoUsuario, HOME_POR_MODULO, type ModuleId } from "@/lib/modules";
import { usePode, type Capacidade } from "@/lib/permissoes";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { loginComVolta } from "@/lib/volta-login";

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * O módulo que esta tela EXIGE. Opcional: quase toda rota já diz o módulo no
   * caminho (`/equipe/*`, `/manejo/*`…) e `moduloExigidoPelaRota` o deduz. A prop
   * existe para a tela cujo caminho não diz nada (uma rota nova fora do prefixo)
   * e para deixar a exigência explícita quando quem lê o `App.tsx` precisa vê-la.
   */
  modulo?: ModuleId;
  /** Tela do módulo Plataforma: exige `user.plataformaAdmin`, senão volta à raiz. */
  plataforma?: boolean;
  /**
   * A capacidade que a tela exige (shared/permissoes.ts). Sem ela: home do
   * módulo + toast. É o ÚNICO eixo de papel aqui — `adminOnly` saiu no PR 4.
   */
  cap?: Capacidade;
}

export function ProtectedRoute({ children, modulo, plataforma = false, cap }: ProtectedRouteProps) {
  const { isAuthenticated, isPlataforma, isLoading, modulesEnabled } = useAuth();
  const [location, setLocation] = useLocation();
  const { toast } = useToast();

  // ⚠️ O PORTÃO DE MÓDULO. Até o Mercado C2 `ProtectedRoute` só perguntava "está
  // logado?", e por isso /cacho/inicio ABRIA para uma org de COMPRADOR: o menu não
  // mostrava o Cacho, mas a rota montava a tela e cada chamada voltava 403. Estar
  // autenticado nunca foi a mesma pergunta que ter o módulo.
  // `usePode` é hook: chamado sempre, com uma capacidade que todo mundo tem quando
  // a rota não exige nenhuma — o resultado só é lido quando `cap` existe.
  const temCap = usePode(cap ?? "org.ver");
  const semCap = !isLoading && isAuthenticated && !!cap && !temCap;

  const exigido = modulo ?? moduloExigidoPelaRota(location);
  // "plataforma" é outro eixo: nunca está em `modulesEnabled`; quem decide é a flag.
  const exigePlataforma = plataforma || exigido === "plataforma";
  const semModulo = !isLoading && isAuthenticated && !exigePlataforma && !!exigido && modulesEnabled.indexOf(exigido) < 0;

  // O aviso sai num efeito (não no render) para disparar UMA vez por redirect —
  // toast dentro do corpo do componente dispararia a cada re-render.
  useEffect(() => {
    if (!semModulo) return;
    toast({
      title: "Módulo não ativo",
      description: "Este módulo não está ativo para a sua fazenda.",
    });
  }, [semModulo]);

  useEffect(() => {
    if (!semCap) return;
    toast({ title: "Sem acesso", description: "Você não tem acesso a esta tela." });
  }, [semCap]);

  // Aguardar validação da sessão com o servidor antes de redirecionar
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Guarda para onde a pessoa ia: o link de um aviso do WhatsApp com a sessão
    // vencida precisa terminar no romaneio, não na home do módulo.
    setLocation(loginComVolta(location, typeof window !== "undefined" ? window.location.search : ""));
    return null;
  }

  if (exigePlataforma && !isPlataforma) {
    setLocation("/", { replace: true } as any);
    return null;
  }

  if (semCap) {
    // Sem a capacidade: a home do módulo da tela (ou a raiz, que decide).
    const destino = exigido && exigido !== "plataforma" && modulesEnabled.indexOf(exigido) >= 0
      ? HOME_POR_MODULO[exigido]
      : (rotaInicialDoUsuario(modulesEnabled, null) ?? "/");
    setLocation(destino, { replace: true } as any);
    return null;
  }

  if (semModulo) {
    // Redirecionar (e não mostrar erro) porque isto não é falha de permissão de
    // quem navega: é link velho, atalho salvo, ou um redirect nosso errado. A
    // pessoa quer trabalhar, e o lugar onde ela trabalha é a home do primeiro
    // módulo que ela tem. `destino` é null só em org sem módulo algum; aí a raiz
    // explica, e mandar para ela é o único caminho que não fecha em círculo.
    const destino = rotaInicialDoUsuario(modulesEnabled, null);
    setLocation(destino ?? "/", { replace: true } as any);
    return null;
  }

  return <>{children}</>;
}
